import React from 'react';
import { injectIntl, WrappedComponentProps } from 'react-intl';

type Props = WrappedComponentProps & {
    children: React.ReactNode;
};

type State = {
    blocked: boolean;
    errorName?: string;
    errorMessage?: string;
};

function isLocalStorageSecurityError(err: unknown): boolean {
    const e = err as any;
    const name = e?.name;
    const message = e?.message;
    const code = e?.code;
    return (
        name === 'SecurityError'
        || code === 18 // DOMException.SECURITY_ERR (legacy)
        || (typeof message === 'string' && message.includes('The operation is insecure'))
    );
}

class StorageErrorBoundary extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        // Test helper: allow forcing the modal via URL query param without breaking localStorage.
        // Example: /fr/?forceStorageError=1
        // (Avoid URLSearchParams for IE11 compatibility)
        const search = typeof window !== 'undefined' ? (window.location.search || '') : '';
        const forced = search.indexOf('forceStorageError=1') !== -1;
        this.state = { blocked: forced };
    }

    static getDerivedStateFromError(error: any): Partial<State> | null {
        if (isLocalStorageSecurityError(error)) {
            return {
                blocked: true,
                errorName: error?.name,
                errorMessage: error?.message,
            };
        }
        return null;
    }

    componentDidCatch(error: any) {
        // Ensure we also block if it happens outside render (extra safety)
        if (isLocalStorageSecurityError(error) && !this.state.blocked) {
            this.setState({
                blocked: true,
                errorName: error?.name,
                errorMessage: error?.message,
            });
        }
    }

    render() {
        const { intl } = this.props;
        if (!this.state.blocked) return this.props.children;

        return (
            <div
                style={{
                    position: 'fixed',
                    top: 0,
                    right: 0,
                    bottom: 0,
                    left: 0,
                    zIndex: 2147483647,
                    background: 'rgba(0,0,0,0.75)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 16,
                }}
            >
                <div
                    style={{
                        width: 'min(560px, 100%)',
                        background: '#fff',
                        borderRadius: 12,
                        padding: 20,
                        boxShadow: '0 10px 30px rgba(0,0,0,0.35)',
                        color: '#111',
                        fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif',
                    }}
                >
                    <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>
                        {intl.formatMessage({ id: 'storage.required.title' })}
                    </div>
                    <div style={{ fontSize: 14, lineHeight: 1.45, marginBottom: 14 }}>
                        {intl.formatMessage({ id: 'storage.required.text' })}
                    </div>
                    <div style={{ fontSize: 12, opacity: 0.75, marginBottom: 14 }}>
                        {this.state.errorName ? `${this.state.errorName}: ` : ''}
                        {this.state.errorMessage || 'SecurityError: The operation is insecure.'}
                    </div>
                    <div style={{ display: 'flex', gap: 10 }}>
                        <button
                            type="button"
                            onClick={() => {
                                // Reload is the most reliable “retry” for storage authorization / privacy context changes
                                window.location.reload();
                            }}
                            style={{
                                appearance: 'none',
                                border: 'none',
                                borderRadius: 10,
                                padding: '10px 14px',
                                background: '#111',
                                color: '#fff',
                                fontWeight: 700,
                                cursor: 'pointer',
                            }}
                        >
                            {intl.formatMessage({ id: 'storage.required.retry' })}
                        </button>
                    </div>
                </div>
            </div>
        );
    }
}

export default injectIntl(StorageErrorBoundary);
